import { memo, useEffect, useId, useState } from 'react';
import mermaid from 'mermaid';
import { useIntl } from 'react-intl';
import Markdown, { Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Box, Link, styled, Typography, useTheme } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { sanitizeMermaidLabels } from '../utils/mermaid';

const MarkdownContainer = styled(Box)(({ theme }) => ({
  ...theme.typography.body2,
  '& p': {
    margin: 0,
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  '& ul, & ol': {
    margin: 0,
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2.5),
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
  '& pre': {
    margin: 0,
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
  },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.875em',
  },
  '& :not(pre) > code': {
    backgroundColor: theme.palette.action.hover,
    padding: '2px 6px',
    borderRadius: theme.shape.borderRadius,
  },
  '& blockquote': {
    margin: 0,
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    borderLeft: `3px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  '& table': {
    borderCollapse: 'collapse',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5, 1),
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: theme.palette.action.hover,
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(2, 0),
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  '& h1': { fontSize: '1.5em' },
  '& h2': { fontSize: '1.25em' },
  '& h3': { fontSize: '1.1em' },
  '& h4, & h5, & h6': { fontSize: '1em' },
}));

type MermaidDiagramProps = {
  code: string;
};

const MermaidDiagram = memo(({ code }: MermaidDiagramProps) => {
  const intl = useIntl();
  const baseId = useId();
  const theme = useTheme();
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    setRenderKey(prev => prev + 1);
  }, [code]);

  useEffect(() => {
    const renderDiagram = async () => {
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: 'base',
          look: 'classic',
          layout: 'dagre',
          securityLevel: 'loose',
          themeVariables: {
            // Base colors
            background: theme.palette.background.paper,
            fontFamily: 'inherit',

            // Primary nodes (default) - light backgrounds, dark text
            primaryColor: '#bbdefb',
            primaryTextColor: '#0d47a1',
            primaryBorderColor: '#1976d2',

            // Secondary nodes
            secondaryColor: '#e0e0e0',
            secondaryTextColor: '#212121',
            secondaryBorderColor: '#9e9e9e',

            // Tertiary nodes
            tertiaryColor: '#f5f5f5',
            tertiaryTextColor: '#212121',
            tertiaryBorderColor: '#bdbdbd',

            // General text and lines
            lineColor: '#616161',
            textColor: '#212121',

            // Flowchart specific
            mainBkg: '#bbdefb',
            nodeBorder: '#1976d2',
            nodeTextColor: '#0d47a1',
            clusterBkg: '#eeeeee',
            clusterBorder: '#bdbdbd',
            titleColor: '#212121',
            edgeLabelBackground: '#ffffff',

            // Sequence diagram
            actorBkg: '#bbdefb',
            actorTextColor: '#0d47a1',
            actorBorder: '#1976d2',
            actorLineColor: '#616161',
            signalColor: '#212121',
            signalTextColor: '#212121',
            labelBoxBkgColor: '#bbdefb',
            labelBoxBorderColor: '#1976d2',
            labelTextColor: '#0d47a1',
            loopTextColor: '#212121',
            noteBkgColor: '#fff9c4',
            noteTextColor: '#212121',
            noteBorderColor: '#fbc02d',
            activationBkgColor: '#e3f2fd',
            activationBorderColor: '#1976d2',

            // Class diagram
            classText: '#212121',

            // State diagram
            labelColor: '#212121',

            // Pie chart
            pie1: '#bbdefb',
            pie2: '#c8e6c9',
            pie3: '#ffe0b2',
            pie4: '#ffcdd2',
            pieTextColor: '#212121',
            pieTitleTextColor: '#212121',
            pieSectionTextColor: '#212121',
            pieStrokeColor: '#ffffff',
          },
        });

        const diagramId = `mermaid-${baseId.replace(/:/g, '')}-${renderKey}`;
        const { svg: renderedSvg } = await mermaid.render(
          diagramId,
          sanitizeMermaidLabels(code)
        );
        setSvg(renderedSvg);
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : intl.formatMessage({
                id: 'features.chat.markdown.diagramRenderFailed',
              })
        );
        setSvg(null);
      }
    };

    renderDiagram();
  }, [code, baseId, renderKey, theme.palette.background.paper, intl]);

  if (error) {
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: 'error.main',
          color: 'error.contrastText',
          borderRadius: borderRadius('s'),
          mb: 1,
        }}
      >
        <Typography variant="body2">
          {intl.formatMessage(
            { id: 'features.chat.markdown.mermaidError' },
            { error }
          )}
        </Typography>
        <Box
          component="pre"
          sx={{ mt: 1, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}
        >
          {code}
        </Box>
      </Box>
    );
  }

  if (!svg) {
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: 'action.hover',
          borderRadius: borderRadius('s'),
          mb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage({ id: 'features.chat.markdown.diagramLoading' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 1,
        p: 2,
        bgcolor: 'background.default',
        borderRadius: borderRadius('s'),
        overflow: 'auto',
        '& svg': {
          maxWidth: '100%',
          height: 'auto',
        },
      }}
      // oxlint-disable-next-line react/no-danger -- oxlint stricter than eslint — sanitized HTML, pre-existing usage
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
});

MermaidDiagram.displayName = 'MermaidDiagram';

type MarkdownRendererProps = {
  content: string;
};

const getChildrenAsString = (children: React.ReactNode): string => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children))
    return children.map(getChildrenAsString).join('');
  return '';
};

const markdownComponents: Partial<Components> = {
  pre({ children }) {
    return <>{children}</>;
  },
  code({ className, children, node, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const codeString = getChildrenAsString(children).replace(/\n$/, '');
    const isInline =
      node?.position?.start.line === node?.position?.end.line && !match;

    if (isInline) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }

    if (match?.[1] === 'mermaid') {
      return <MermaidDiagram code={codeString} />;
    }

    return (
      <SyntaxHighlighter
        // eslint-disable-next-line no-restricted-syntax -- style passed to third-party component, cannot use sx
        style={oneDark}
        language={match?.[1] ?? 'text'}
        PreTag="div"
      >
        {codeString}
      </SyntaxHighlighter>
    );
  },
  a({ href, children }) {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: 'primary.main' }}
      >
        {children}
      </Link>
    );
  },
};

export const MarkdownRenderer = memo(({ content }: MarkdownRendererProps) => {
  return (
    <MarkdownContainer>
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={markdownComponents}
      >
        {content}
      </Markdown>
    </MarkdownContainer>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';
